/* Everything Automotive — Share App button + branded share modal
 * Lives at: frontend/src/components/ShareApp.js
 *
 * No external React libraries — the QR is generated as an image with a high
 * error-correction level, and the logo is overlaid in the center.
 *
 * The modal sits ABOVE the site navbar (high z-index) and is anchored from the
 * top with safe-area padding + scroll, so its header and close button are never
 * cut off or buried on small screens.
 */

import React, { useState } from "react";

const SHARE_URL = "https://www.everything-automotive.com";
const SHARE_TEXT =
  "Check out Everything Automotive — buy & sell cars and parts, book services, and get repairs, all in one place. Install free:";

export default function ShareApp() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrFailed, setQrFailed] = useState(false);

  const fullMessage = `${SHARE_TEXT} ${SHARE_URL}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(fullMessage)}`;

  const qrSrc =
    "https://api.qrserver.com/v1/create-qr-code/?size=260x260&qzone=2&ecc=H" +
    "&color=08182e&bgcolor=ffffff&data=" +
    encodeURIComponent(SHARE_URL);

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Everything Automotive",
          text: SHARE_TEXT,
          url: SHARE_URL,
        });
      } catch (err) {
        // User cancelled — ignore.
      }
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      window.prompt("Copy this link to share:", SHARE_URL);
    }
  };

  return (
    <>
      <button className="ea-share-btn" onClick={() => setOpen(true)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        Share
      </button>

      {open && (
        <div
          className="ea-share-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Share Everything Automotive"
          onClick={() => setOpen(false)}
        >
          <div className="ea-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ea-share-head">
              <img className="ea-share-logo" src="/logo192.png" alt="Everything Automotive logo" />
              <div>
                <p className="ea-share-title">Share Everything Automotive</p>
                <p className="ea-share-sub">Send the app to friends and family.</p>
              </div>
              <button className="ea-share-x" aria-label="Close" onClick={() => setOpen(false)}>
                ×
              </button>
            </div>

            <div className="ea-share-actions">
              <a className="ea-share-action ea-share-whatsapp" href={whatsappHref} target="_blank" rel="noopener noreferrer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.39c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.9-4.44 9.9-9.9S17.5 2 12.04 2zm0 18.1c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23 4.54 0 8.23 3.69 8.23 8.23 0 4.54-3.69 8.23-8.23 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.48-1.38-1.73-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42-.14 0-.31-.02-.47-.02-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z" />
                </svg>
                Share via WhatsApp
              </a>

              {typeof navigator !== "undefined" && navigator.share && (
                <button className="ea-share-action ea-share-more" onClick={handleNativeShare}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  More apps (SMS, Telegram…)
                </button>
              )}

              <button className="ea-share-action ea-share-copy" onClick={handleCopy}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                {copied ? "Link copied ✓" : "Copy link"}
              </button>
            </div>

            <div className="ea-share-qr-wrap">
              {!qrFailed ? (
                <div className="ea-share-qr">
                  <img className="ea-share-qr-img" src={qrSrc} alt="QR code for everything-automotive.com" onError={() => setQrFailed(true)} />
                  <span className="ea-share-qr-logo">
                    <img src="/logo192.png" alt="" />
                  </span>
                </div>
              ) : (
                <p className="ea-share-qr-fallback">{SHARE_URL}</p>
              )}
              <p className="ea-share-qr-cap">Scan to open Everything Automotive</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ea-share-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255, 255, 255, 0.12); color: #eaf2ff;
          border: 1px solid rgba(255, 255, 255, 0.25); border-radius: 12px;
          padding: 12px 22px; font-size: 0.95rem; font-weight: 600; cursor: pointer;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .ea-share-btn:hover { background: rgba(255, 255, 255, 0.2); transform: translateY(-1px); }
        .ea-share-btn:focus-visible { outline: 3px solid #9cc4ff; outline-offset: 2px; }

        .ea-share-overlay {
          position: fixed; inset: 0; background: rgba(5, 12, 28, 0.72);
          display: flex;
          align-items: flex-start;        /* anchor to top so the header is never cut off */
          justify-content: center;
          overflow-y: auto;               /* scroll if the modal is taller than the screen */
          -webkit-overflow-scrolling: touch;
          z-index: 100000;                /* above the site navbar */
          padding: 16px;
          padding-top: max(84px, calc(env(safe-area-inset-top) + 76px));
          padding-bottom: max(24px, env(safe-area-inset-bottom));
        }
        .ea-share-modal {
          position: relative;
          background: #fff; color: #15243f; border-radius: 18px; padding: 22px;
          max-width: 380px; width: 100%; text-align: left;
          box-shadow: 0 20px 60px rgba(5, 12, 28, 0.5);
          margin: 0 auto;
        }
        .ea-share-head {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 18px; padding-right: 44px;
        }
        .ea-share-logo { width: 48px; height: 48px; border-radius: 12px; background: #08182e; object-fit: contain; padding: 3px; flex-shrink: 0; }
        .ea-share-title { margin: 0; font-size: 1.05rem; font-weight: 800; }
        .ea-share-sub { margin: 2px 0 0; font-size: 0.8rem; color: #5b6b85; }
        .ea-share-x {
          position: absolute; top: 12px; right: 12px;
          width: 38px; height: 38px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          background: #eef2fb; border: none;
          font-size: 1.5rem; line-height: 1; color: #1e3a6b; cursor: pointer;
          transition: background 0.15s ease;
        }
        .ea-share-x:hover { background: #dde6f6; color: #15243f; }
        .ea-share-x:focus-visible { outline: 3px solid #9cc4ff; outline-offset: 2px; }

        .ea-share-actions { display: flex; flex-direction: column; gap: 10px; }
        .ea-share-action {
          display: flex; align-items: center; gap: 10px; width: 100%; border: none;
          border-radius: 12px; padding: 13px 16px; font-size: 0.92rem; font-weight: 700;
          cursor: pointer; text-decoration: none; transition: transform 0.12s ease, filter 0.12s ease;
        }
        .ea-share-action:hover { transform: translateY(-1px); filter: brightness(0.97); }
        .ea-share-whatsapp { background: #25d366; color: #053d1c; }
        .ea-share-more { background: #0b63d6; color: #fff; }
        .ea-share-copy { background: #eef2fb; color: #1e3a6b; }

        .ea-share-qr-wrap { margin-top: 18px; padding-top: 18px; border-top: 1px solid #eef1f6; text-align: center; }
        .ea-share-qr {
          position: relative; display: inline-block; line-height: 0;
          border-radius: 14px; border: 1px solid #eef1f6; padding: 10px; background: #fff;
        }
        .ea-share-qr-img { width: 180px; height: 180px; display: block; }
        .ea-share-qr-logo {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 44px; height: 44px; background: #fff; border-radius: 10px;
          padding: 4px; box-shadow: 0 0 0 3px #fff; display: flex; align-items: center; justify-content: center;
        }
        .ea-share-qr-logo img { width: 100%; height: 100%; object-fit: contain; border-radius: 6px; }
        .ea-share-qr-fallback { font-size: 0.85rem; color: #1e3a6b; word-break: break-all; font-weight: 700; }
        .ea-share-qr-cap { margin: 8px 0 0; font-size: 0.78rem; color: #7a88a0; }

        @media (prefers-reduced-motion: reduce) {
          .ea-share-btn, .ea-share-btn:hover,
          .ea-share-action, .ea-share-action:hover { transition: none; transform: none; }
        }
      `}</style>
    </>
  );
}
